import { createFileRoute, Link, useLocation } from '@tanstack/react-router'
import {
  Activity,
  ArrowRight,
  Bell,
  ChartNoAxesCombined,
  CheckCircle2,
  DatabaseZap,
  Eye,
  Gauge,
  LayoutDashboard,
  LineChart,
  MonitorCheck,
  Network,
  Radar,
  Route as RouteIcon,
  SlidersHorizontal,
} from 'lucide-react'
import { motion } from 'motion/react'
import { LandingPageLayout } from '@/components/landing/LandingPageLayout'
import { SectionScroller } from '@/components/landing/SectionScroller'
import { designSystem } from '@/lib/design-system'
import { getLocaleFromPath, withLocalePath } from '@/lib/i18n-utils'
import { cn } from '@/lib/utils'
import { GridSpotlight } from '@/components/ui/grid-spotlight'
import { ServiceHeroVisual } from '@/components/services/ServiceHeroVisual'

export const Route = createFileRoute('/{-$locale}/services/data-processing-analytics')({
  head: () => ({
    meta: [
      { title: 'Data Processing & Analytics — Ponte Solutions' },
      {
        name: 'description',
        content:
          'Data processing and analytics by Ponte Solutions. Telemetry normalization, analytics pipelines, dashboards, alerting, and data models for connected hardware products.',
      },
      { property: 'og:title', content: 'Data Processing & Analytics — Ponte Solutions' },
      {
        property: 'og:description',
        content:
          'Data processing and analytics by Ponte Solutions. Telemetry normalization, analytics pipelines, dashboards, alerting, and data models for connected hardware products.',
      },
    ],
  }),
  component: DataProcessingAnalyticsPage,
})

const visibilitySignals = [
  {
    icon: Eye,
    title: 'Raw telemetry is not business-ready.',
    text: 'Device events arrive with inconsistent names, units, timing, and context before they can support service or product decisions.',
  },
  {
    icon: Bell,
    title: 'Signals need context before action.',
    text: 'Thresholds, anomalies, and trends need asset, site, customer, and service context before teams can trust the next step.',
  },
  {
    icon: ChartNoAxesCombined,
    title: 'Reports lag when pipelines are weak.',
    text: 'Teams end up with spreadsheets and one-off queries instead of repeatable analytics products built on trusted data models.',
  },
]

const cockpitPanels = [
  { label: 'Normalized Events', value: '1.8M', tone: 'text-accent' },
  { label: 'Active Rules', value: '42', tone: 'text-foreground' },
  { label: 'Data Quality', value: '98%', tone: 'text-primary' },
]

const workflowSteps = [
  {
    icon: DatabaseZap,
    title: 'Telemetry Model',
    text: 'Normalize raw events into assets, sites, metrics, states, incidents, and operating windows.',
  },
  {
    icon: LayoutDashboard,
    title: 'Role Views',
    text: 'Give service, product, management, and customer teams the views they need without one overloaded dashboard.',
  },
  {
    icon: SlidersHorizontal,
    title: 'Signal Rules',
    text: 'Tune alert logic with hysteresis, deduplication, severity, and escalation paths.',
  },
  {
    icon: RouteIcon,
    title: 'Workflow Routing',
    text: 'Push the right events into service tickets, customer portals, email, Teams, BI, or operations tools.',
  },
]

const deliverySteps = [
  {
    eyebrow: '01',
    title: 'Analytics Map',
    text: 'We define the questions each team needs answered and map them to your available device, site, customer, and service data.',
  },
  {
    eyebrow: '02',
    title: 'Pipeline Pilot',
    text: 'We build a production-shaped processing path, dashboard, and alert loop against real or representative telemetry, then tune it with the people who will use it.',
  },
  {
    eyebrow: '03',
    title: 'Operational Rollout',
    text: 'We deploy access, reporting, notifications, governance, and runbooks so analytics becomes part of daily service operations.',
  },
]

const outcomes = [
  'Telemetry models your teams can trust',
  'Dashboards and reports built from clean data',
  'Signal logic connected to action',
  'A clean data model for Action Intelligence & Automation',
]

const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1 } },
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: 'easeOut' } },
}

function onSectionMouseMove(e: React.MouseEvent<HTMLElement>) {
  const rect = e.currentTarget.getBoundingClientRect()
  e.currentTarget.style.setProperty('--spot-x', `${e.clientX - rect.left}px`)
  e.currentTarget.style.setProperty('--spot-y', `${e.clientY - rect.top}px`)
}

function onCardMouseMove(e: React.MouseEvent<HTMLElement>) {
  const rect = e.currentTarget.getBoundingClientRect()
  e.currentTarget.style.setProperty('--mouse-x', `${e.clientX - rect.left}px`)
  e.currentTarget.style.setProperty('--mouse-y', `${e.clientY - rect.top}px`)
}

function CardSpotlight() {
  return (
    <>
      <div className="pointer-events-none absolute inset-0 z-0 rounded-xl opacity-0 backdrop-blur-[1px] transition-opacity duration-200 group-hover:opacity-100" />
      <div className="pointer-events-none absolute inset-0 z-0 rounded-xl bg-accent/[0.08] opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
      <div
        className="pointer-events-none absolute inset-0 z-0 rounded-xl opacity-0 transition-opacity duration-200 group-hover:opacity-100"
        style={{
          background:
            'radial-gradient(240px circle at var(--mouse-x, 50%) var(--mouse-y, 50%), oklch(68% .095 52 / 0.2), transparent 70%)',
        }}
      />
    </>
  )
}

function DataProcessingAnalyticsPage() {
  return (
    <LandingPageLayout>
      <div className="ponte-service-page">
        <SectionScroller labels={['Analytics', 'Signals', 'Model', 'Workflow', 'Start']}>
          <DataProcessingHeroSection />
          <VisibilityGapSection />
          <CockpitSection />
          <WorkflowSection />
          <DataProcessingCtaSection />
        </SectionScroller>
      </div>
    </LandingPageLayout>
  )
}

function DataProcessingHeroSection() {
  const location = useLocation()
  const locale = getLocaleFromPath(location.pathname)

  return (
    <section
      className="ponte-service-section ponte-service-section--hero relative isolate flex min-h-[calc(100svh-4rem)] items-center overflow-hidden px-4 py-10 text-white sm:px-6 lg:px-8"
      style={{ '--spot-x': '50%', '--spot-y': '50%' } as React.CSSProperties}
      onMouseMove={onSectionMouseMove}
    >
      <GridSpotlight />
      <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-10 lg:grid-cols-[0.9fr_1.1fr]">
        <motion.div
          className="max-w-3xl py-10"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          <motion.div
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/8 px-3 py-1.5 text-xs font-medium text-white/75"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.1 }}
          >
            <Activity className="h-3.5 w-3.5 text-accent" />
            Data Processing & Analytics
          </motion.div>

          <motion.h1
            className={cn(
              designSystem.typography.heading.h1,
              'max-w-4xl text-balance text-[clamp(2.8rem,7vw,5.5rem)] font-semibold leading-[0.92] tracking-normal text-white',
            )}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.2, ease: 'easeOut' }}
          >
            Turn raw telemetry into{' '}
            <span className="text-accent">usable analytics.</span>
          </motion.h1>

          <motion.p
            className="mt-7 max-w-2xl text-pretty text-lg leading-8 text-white/72 md:text-xl"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            Ponte builds the processing and analytics layer around your connected products:
            normalized telemetry, trusted data models, dashboards, alerts, and reports your teams
            can act on.
          </motion.p>

          <motion.div
            className="mt-9 flex flex-col gap-3 sm:flex-row"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.4 }}
          >
            <Link
              to={withLocalePath('/contact', locale)}
              className="landing-cta-primary inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold"
            >
              Discuss Analytics
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to={withLocalePath('/services/action-intelligence', locale)}
              className="landing-cta-secondary inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold"
            >
              Next: Action Intelligence
              <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </motion.div>

        <ServiceHeroVisual service="analytics" />
      </div>
    </section>
  )
}

function VisibilityGapSection() {
  return (
    <section className="ponte-service-section ponte-service-section--deep relative flex min-h-[calc(100svh-4rem)] items-center overflow-hidden px-4 py-14 sm:px-6 lg:px-8">
      <div
        aria-hidden
        className="absolute inset-0 bg-[radial-gradient(circle_at_20%_60%,rgba(214,151,101,.1),transparent_40%)]"
      />

      <motion.div
        className="relative z-10 mx-auto grid w-full max-w-7xl gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-center"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: '-80px' }}
        variants={containerVariants}
      >
        <motion.div variants={itemVariants}>
          <span className={cn(designSystem.typography.display.eyebrow, 'mb-3 block dark:font-mono dark:text-primary/70')}>
            Processing Gap
          </span>
          <h2
            className={cn(
              designSystem.typography.heading.h1,
              'max-w-xl text-balance text-3xl font-semibold tracking-normal text-white sm:text-4xl lg:text-5xl',
            )}
          >
            Data in the cloud is not the same as{' '}
            <span className="text-accent">intelligence in the business.</span>
          </h2>
          <p className="mt-5 max-w-xl text-base leading-8 text-white/60">
            Data Processing & Analytics turns device events into reliable information: what changed,
            what matters, what needs attention, and which team should act next.
          </p>
        </motion.div>

        <motion.div className="grid gap-4" variants={containerVariants}>
          {visibilitySignals.map((item) => (
            <motion.div
              key={item.title}
              variants={itemVariants}
              className="group relative overflow-hidden rounded-xl border border-white/[0.08] bg-white/[0.03] p-5 backdrop-blur-sm transition-all duration-300 hover:border-accent/40"
              style={{ '--mouse-x': '50%', '--mouse-y': '50%' } as React.CSSProperties}
              onMouseMove={onCardMouseMove}
            >
              <CardSpotlight />
              <div className="relative z-10 grid gap-4 sm:grid-cols-[3rem_1fr]">
                <div className="flex h-12 w-12 items-center justify-center rounded-md bg-accent/10 text-accent transition-colors duration-300 group-hover:bg-accent group-hover:text-accent-foreground">
                  <item.icon className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">{item.title}</h3>
                  <p className="mt-2 text-sm leading-7 text-white/55">{item.text}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  )
}

function CockpitSection() {
  return (
    <section
      className="ponte-service-section ponte-service-section--mesh relative flex min-h-[calc(100svh-4rem)] items-center overflow-hidden px-4 py-14 text-white sm:px-6 lg:px-8"
      style={{ '--spot-x': '50%', '--spot-y': '50%' } as React.CSSProperties}
      onMouseMove={onSectionMouseMove}
    >
      <GridSpotlight />
      <div
        aria-hidden
        className="absolute inset-0 bg-[radial-gradient(circle_at_50%_38%,rgba(214,151,101,.16),transparent_36%)]"
      />

      <motion.div
        className="relative z-10 mx-auto grid w-full max-w-7xl gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-center"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: '-80px' }}
        variants={containerVariants}
      >
        <motion.div variants={itemVariants}>
          <span className={cn(designSystem.typography.display.eyebrow, 'mb-3 block dark:font-mono dark:text-primary/70')}>
            Analytics Workspace
          </span>
          <h2
            className={cn(
              designSystem.typography.heading.h1,
              'max-w-xl text-balance text-3xl font-semibold tracking-normal text-white sm:text-4xl lg:text-5xl',
            )}
          >
            Data products should answer{' '}
            <span className="text-accent">operational questions.</span>
          </h2>
          <p className="mt-5 max-w-xl text-base leading-8 text-white/55">
            We design around data contracts, roles, and decisions, not chart inventory. Operators
            need current state. Service leads need risk and priority. Management needs trend, cost,
            and customer context.
          </p>
        </motion.div>

        <motion.div
          variants={itemVariants}
          className="rounded-2xl border border-white/[0.08] bg-white/[0.035] p-4 shadow-2xl shadow-black/20 backdrop-blur-sm"
        >
          <div className="rounded-xl border border-border/70 bg-card/95 p-4 shadow-sm dark:border-white/[0.08] dark:bg-[#0a1116] dark:shadow-none">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <MonitorCheck className="h-4 w-4 text-accent" />
                <span className="text-sm font-semibold text-foreground">Processing Operations</span>
              </div>
              <span className="rounded-full border border-accent/30 bg-accent/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-accent">
                Live
              </span>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              {cockpitPanels.map((panel) => (
                <div
                  key={panel.label}
                  className="rounded-lg border border-border/70 bg-background/60 p-3 dark:border-white/[0.07] dark:bg-white/[0.035]"
                >
                  <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">{panel.label}</p>
                  <p className={cn('mt-2 text-2xl font-semibold', panel.tone)}>{panel.value}</p>
                </div>
              ))}
            </div>

            <div className="mt-4 grid gap-3 lg:grid-cols-[1.4fr_0.9fr]">
              <div className="rounded-lg border border-border/70 bg-background/60 p-4 dark:border-white/[0.07] dark:bg-white/[0.03]">
                <div className="mb-4 flex items-center justify-between">
                  <span className="text-xs font-semibold text-muted-foreground">Processing Throughput</span>
                  <LineChart className="h-4 w-4 text-accent" />
                </div>
                <div className="relative h-36 overflow-hidden rounded-md border border-border/50 bg-card/55 px-3 py-3 dark:border-white/[0.05] dark:bg-black/10">
                  <svg
                    viewBox="0 0 360 132"
                    className="h-full w-full overflow-visible text-accent"
                    aria-hidden="true"
                  >
                    <defs>
                      <linearGradient id="processing-throughput-area" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="currentColor" stopOpacity="0.28" />
                        <stop offset="72%" stopColor="currentColor" stopOpacity="0.05" />
                        <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    {[28, 58, 88].map((y) => (
                      <line
                        key={y}
                        x1="0"
                        y1={y}
                        x2="360"
                        y2={y}
                        stroke="currentColor"
                        strokeOpacity="0.12"
                        strokeDasharray="4 8"
                      />
                    ))}
                    <path
                      d="M0 102 C34 96 42 78 76 82 C108 86 114 52 150 58 C186 64 196 36 232 44 C270 52 282 28 318 34 C340 38 350 30 360 26 L360 132 L0 132 Z"
                      fill="url(#processing-throughput-area)"
                    />
                    <path
                      d="M0 102 C34 96 42 78 76 82 C108 86 114 52 150 58 C186 64 196 36 232 44 C270 52 282 28 318 34 C340 38 350 30 360 26"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                    {[76, 150, 232, 318].map((x, index) => (
                      <circle
                        key={x}
                        cx={x}
                        cy={[82, 58, 44, 34][index]}
                        r="4"
                        fill="currentColor"
                        stroke="var(--card)"
                        strokeWidth="3"
                      />
                    ))}
                  </svg>
                  <div className="absolute inset-x-3 bottom-2 grid grid-cols-4 text-[9px] font-semibold uppercase tracking-[0.12em] text-muted-foreground/70">
                    <span>Ingest</span>
                    <span>Normalize</span>
                    <span>Validate</span>
                    <span>Route</span>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-border/70 bg-background/60 p-4 dark:border-white/[0.07] dark:bg-white/[0.03]">
                <div className="mb-4 flex items-center justify-between">
                  <span className="text-xs font-semibold text-muted-foreground">Data Quality</span>
                  <Radar className="h-4 w-4 text-accent" />
                </div>
                <div className="space-y-3">
                  {[['Clean', '86%'], ['Needs Mapping', '10%'], ['Review', '4%']].map(([label, value]) => (
                    <div key={label}>
                      <div className="mb-1 flex justify-between text-xs text-muted-foreground">
                        <span>{label}</span>
                        <span>{value}</span>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-muted">
                        <div className="h-full rounded-full bg-accent" style={{ width: value }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </section>
  )
}

function WorkflowSection() {
  return (
    <section className="ponte-service-section ponte-service-section--deep relative flex min-h-[calc(100svh-4rem)] items-center overflow-hidden px-4 py-14 text-white sm:px-6 lg:px-8">
      <motion.div
        className="relative z-10 mx-auto w-full max-w-7xl"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: '-80px' }}
        variants={containerVariants}
      >
        <motion.div className="mx-auto max-w-3xl text-center" variants={itemVariants}>
          <span className={cn(designSystem.typography.display.eyebrow, 'mb-3 block dark:font-mono dark:text-primary/70')}>
            Alert and Reporting Loop
          </span>
          <h2
            className={cn(
              designSystem.typography.heading.h1,
              'text-balance text-3xl font-semibold tracking-normal text-white sm:text-4xl lg:text-5xl',
            )}
          >
            Analytics has to reach the{' '}
            <span className="text-accent">people who act.</span>
          </h2>
          <p className="mt-5 text-base leading-8 text-white/55">
            The dashboard is only one surface. The same analytics model should feed alerts, reports,
            service workflows, customer views, and the future action layer.
          </p>
        </motion.div>

        <div className="mt-12 grid gap-4 lg:grid-cols-4">
          {workflowSteps.map((step, index) => (
            <motion.div
              key={step.title}
              variants={itemVariants}
              className="group relative overflow-hidden rounded-xl border border-white/[0.08] bg-white/[0.025] p-5 backdrop-blur-sm transition-all duration-300 hover:border-accent/30"
              style={{ '--mouse-x': '50%', '--mouse-y': '50%' } as React.CSSProperties}
              onMouseMove={onCardMouseMove}
            >
              {index < workflowSteps.length - 1 && (
                <div className="pointer-events-none absolute -right-2 top-[2.75rem] z-20 hidden h-[2px] w-4 bg-gradient-to-r from-accent/40 to-transparent lg:block" />
              )}
              <CardSpotlight />
              <div className="relative z-10">
                <div className="mb-8 flex items-center justify-between">
                  <div className="flex h-11 w-11 items-center justify-center rounded-md bg-accent text-accent-foreground transition-transform duration-300 group-hover:scale-105">
                    <step.icon className="h-5 w-5" />
                  </div>
                  <span className="font-mono text-xs text-white/30">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-white transition-colors duration-300 group-hover:text-accent">
                  {step.title}
                </h3>
                <p className="mt-3 text-sm leading-7 text-white/50">{step.text}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div className="mt-8 grid gap-4 lg:grid-cols-3" variants={containerVariants}>
          {deliverySteps.map((step) => (
            <motion.div
              key={step.title}
              variants={itemVariants}
              className="group relative overflow-hidden rounded-xl border border-white/[0.08] bg-white/[0.025] p-5 backdrop-blur-sm transition-all duration-300 hover:border-accent/25"
              style={{ '--mouse-x': '50%', '--mouse-y': '50%' } as React.CSSProperties}
              onMouseMove={onCardMouseMove}
            >
              <CardSpotlight />
              <div className="relative z-10">
                <p className="font-mono text-xs font-semibold text-accent">{step.eyebrow}</p>
                <h3 className="mt-4 text-lg font-semibold text-white">{step.title}</h3>
                <p className="mt-2 text-sm leading-7 text-white/50">{step.text}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  )
}

function DataProcessingCtaSection() {
  const location = useLocation()
  const locale = getLocaleFromPath(location.pathname)

  return (
    <section
      className="ponte-service-section ponte-service-section--cta relative flex min-h-[calc(100svh-4rem)] items-center overflow-hidden px-4 py-14 text-white sm:px-6 lg:px-8"
      style={{ '--spot-x': '50%', '--spot-y': '50%' } as React.CSSProperties}
      onMouseMove={onSectionMouseMove}
    >
      <GridSpotlight gridSize={88} baseOpacity={0.12} highlightOpacity={0.22} />
      <div
        aria-hidden
        className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(214,151,101,.14),transparent_45%)]"
      />

      <motion.div
        className="relative z-10 mx-auto grid w-full max-w-7xl gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-center"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: '-80px' }}
        variants={containerVariants}
      >
        <motion.div variants={itemVariants}>
          <span className={cn(designSystem.typography.display.eyebrow, 'mb-3 block dark:font-mono dark:text-primary/70')}>
            What Data Processing Unlocks
          </span>
          <h2
            className={cn(
              designSystem.typography.heading.h1,
              'max-w-2xl text-balance text-3xl font-semibold tracking-normal text-white sm:text-4xl lg:text-5xl',
            )}
          >
            Once teams trust the data,{' '}
            <span className="text-accent">service decisions move faster.</span>
          </h2>
          <p className="mt-5 max-w-2xl text-base leading-8 text-white/55">
            Data Processing & Analytics gives your connected product program the operating layer:
            trusted metrics, reports, alert paths, and clean historical context for action
            intelligence and automation.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to={withLocalePath('/contact', locale)}
              className="landing-cta-primary inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold"
            >
              Start an Analytics Project
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to={withLocalePath('/services/action-intelligence', locale)}
              className="landing-cta-secondary inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold"
            >
              Explore Action Intelligence
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </motion.div>

        <motion.div
          variants={itemVariants}
          className="group relative overflow-hidden rounded-xl border border-white/[0.08] bg-white/[0.025] p-6 backdrop-blur-sm transition-all duration-300 hover:border-accent/25"
          style={{ '--mouse-x': '50%', '--mouse-y': '50%' } as React.CSSProperties}
          onMouseMove={onCardMouseMove}
        >
          <CardSpotlight />
          <div className="relative z-10">
            <div className="mb-6 flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-md bg-accent text-accent-foreground">
                <Gauge className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold text-white">Analytics outcomes</h3>
                <p className="text-sm text-white/50">A trusted information layer for the installed base.</p>
              </div>
            </div>

            <div className="grid gap-3">
              {outcomes.map((outcome) => (
                <div
                  key={outcome}
                  className="flex items-start gap-3 rounded-lg bg-white/[0.04] p-3 transition-colors duration-200 hover:bg-white/[0.07]"
                >
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                  <span className="text-sm font-medium text-white/85">{outcome}</span>
                </div>
              ))}
            </div>

            <div className="mt-6 grid grid-cols-3 gap-2 text-center">
              {[
                { icon: Activity, label: 'Live' },
                { icon: Network, label: 'Fleet' },
                { icon: Bell, label: 'Alerts' },
              ].map((item) => (
                <div
                  key={item.label}
                  className="rounded-lg border border-white/[0.06] bg-white/[0.03] p-3 transition-all duration-200 hover:border-accent/20 hover:bg-white/[0.05]"
                >
                  <item.icon className="mx-auto h-4 w-4 text-accent" />
                  <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-white/50">
                    {item.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </section>
  )
}
